import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { eventDetailsSummaryStyles as styles } from './EventDetailsSummary.styles';

const MOBILE_MAX_WIDTH = 768;

function useIsMobileViewport() {
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX_WIDTH : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateViewport = () => {
      setIsMobileViewport(window.innerWidth <= MOBILE_MAX_WIDTH);
    };

    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  return isMobileViewport;
}

export type CourseScheduleDay = {
  dayLabel: string;
  timeRange: string;
  trainers: string;
};

export type EventDetailsSummaryData = {
  eventDate?: string;
  showTimeRow?: boolean;
  timeRow?: string;
  showTimeslotMeta?: boolean;
  bookingsLine?: string;
  showWaitingListBlock?: boolean;
  waitingListLine?: string;
  waitingPositionLine?: string | null;
  levelLine?: string | null;
  trainersLine?: string | null;
  facilityLine?: string | null;
  showCourseSchedule?: boolean;
  viewScheduleLabel?: string;
  trainersLabel?: string;
  closeLabel?: string;
  scheduleDays?: CourseScheduleDay[];
  showAttendancePolicy?: boolean;
  attendancePolicyTitle?: string;
  attendancePolicyMessage?: string;
};

type Props = {
  data: EventDetailsSummaryData;
};

function DetailLine({ text }: { text?: string | null }) {
  if (!text) {
    return null;
  }

  return <Text style={styles.mutedText}>{text}</Text>;
}

function EventTimeRow({ data }: { data: EventDetailsSummaryData }) {
  if (!data.showTimeRow) {
    return null;
  }

  return <DetailLine text={data.timeRow} />;
}

function WaitingListDetails({ data }: { data: EventDetailsSummaryData }) {
  if (!data.showWaitingListBlock) {
    return null;
  }

  return (
    <>
      <DetailLine text={data.waitingListLine} />
      <DetailLine text={data.waitingPositionLine} />
    </>
  );
}

function TimeslotMetaDetails({ data }: { data: EventDetailsSummaryData }) {
  if (!data.showTimeslotMeta) {
    return null;
  }

  return (
    <>
      <DetailLine text={data.bookingsLine} />
      <WaitingListDetails data={data} />
      <DetailLine text={data.levelLine} />
      <DetailLine text={data.trainersLine} />
      <DetailLine text={data.facilityLine} />
    </>
  );
}

function AttendancePolicyDetails({ data }: { data: EventDetailsSummaryData }) {
  if (!data.showAttendancePolicy) {
    return null;
  }

  return (
    <View style={styles.attendanceSection}>
      <Text style={styles.mutedText}>{data.attendancePolicyTitle}</Text>
      <Text style={styles.mutedText}>{data.attendancePolicyMessage}</Text>
    </View>
  );
}

function CourseScheduleSection({
  data,
  isMobileViewport,
  scheduleOpen,
  onOpenSchedule,
  onCloseSchedule,
}: {
  data: EventDetailsSummaryData;
  isMobileViewport: boolean;
  scheduleOpen: boolean;
  onOpenSchedule: () => void;
  onCloseSchedule: () => void;
}) {
  if (!data.showCourseSchedule) {
    return null;
  }

  return (
    <CourseScheduleBlock
      data={data}
      isMobileViewport={isMobileViewport}
      scheduleOpen={scheduleOpen}
      onOpenSchedule={onOpenSchedule}
      onCloseSchedule={onCloseSchedule}
    />
  );
}

function CourseScheduleBlock({
  data,
  isMobileViewport,
  scheduleOpen,
  onOpenSchedule,
  onCloseSchedule,
}: {
  data: EventDetailsSummaryData;
  isMobileViewport: boolean;
  scheduleOpen: boolean;
  onOpenSchedule: () => void;
  onCloseSchedule: () => void;
}) {
  const scheduleDays = data.scheduleDays ?? [];
  const dropdownClassName =
    isMobileViewport && scheduleOpen ? 'more-dropdown active' : 'more-dropdown';

  const handleContainerClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (isMobileViewport) {
      onOpenSchedule();
    }
  };

  return (
    <div className="more" onClick={handleContainerClick}>
      <div>{data.viewScheduleLabel}</div>
      <ul className={dropdownClassName}>
        {scheduleDays.map((day, index) => (
          <li key={`${day.dayLabel}-${index}`}>
            <b>{day.dayLabel}</b>
            {` ${day.timeRange}`}
            <br />
            <b>{data.trainersLabel}:</b>
            {` ${day.trainers}`}
          </li>
        ))}
        {isMobileViewport ? (
          <li>
            <button
              type="button"
              className="button-colored"
              onClick={(event) => {
                event.stopPropagation();
                onCloseSchedule();
              }}
            >
              {data.closeLabel}
            </button>
          </li>
        ) : null}
      </ul>
    </div>
  );
}

export function EventDetailsSummary({ data }: Props) {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const isMobileViewport = useIsMobileViewport();

  useEffect(() => {
    setScheduleOpen(false);
  }, [isMobileViewport]);

  if (!data) {
    return null;
  }

  const handleOpenSchedule = () => {
    if (isMobileViewport) {
      setScheduleOpen(true);
    }
  };

  return (
    <View style={styles.wrapper}>
      <CourseScheduleSection
        data={data}
        isMobileViewport={isMobileViewport}
        scheduleOpen={scheduleOpen}
        onOpenSchedule={handleOpenSchedule}
        onCloseSchedule={() => setScheduleOpen(false)}
      />

      <View>
        <DetailLine text={data.eventDate} />
        <EventTimeRow data={data} />
        <TimeslotMetaDetails data={data} />
      </View>

      <AttendancePolicyDetails data={data} />
    </View>
  );
}
